"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : new P(function (resolve) { resolve(result.value); }).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
/**
 * Rpc implements an remote-procedure-call interface on top of a simple messaging interface.
 *
 * The user must provide the messaging between two endpoints, and in return gets the ability to
 * register interfaces or functions at either endpoint, and call them from the other side. For
 * messaging, the user must supply a sendMessage() function to send messages to the other side,
 * and must call rpc.receiveMessage(msg) whenever a message is received.
 *
 * E.g.
 *    rpc.registerImpl<MyInterface>("some-name", new MyInterfaceImpl(), descMyInterfaceImpl);
 *    rpc.getStub<MyInterface>("some-name", descMyInterfaceImpl)
 *          => returns a stub implemeting MyInterface
 *
 * Calls to the generated stub get turned into messages across the channel, and then call to the
 * implementation object registered on the other side. Both return values and exceptions get
 * passed back over the channel, and cause the promise from the stub to be resolved or rejected.
 *
 * Note that the stub interface returns Promises for all methods.
 *
 * Rpc library supports ts-interface-checker descriptors for the interfaces, to allow validation.
 * You may skip it by passing in `rpc.unchecked` where a descriptor is expected; it will skip
 * checks and you will not get descriptive errors.
 *
 * The string name used to register and use an implementation allows for the same Rpc object to be
 * used to expose multiple interfaces, or different implementations of the same interface.
 *
 * Messaging
 * ---------
 * Rpc also supports a messaging interface, with postMessage() to send arbitrary messages, and an
 * EventEmitter interface for "message" events to receive them, e.g. on("message", ...). So if you
 * need to multiplex non-Rpc messages over the same channel, Rpc class does it for you.
 *
 * Cleanup
 * -------
 * If the channel is closed or had an error, and will no longer be used, the user of Rpc must
 * call rpc.close() to reject any calls waiting for an answer.
 *
 * If a particular stub for a remote API is no longer needed, user may call rpc.discardStub(stub)
 * to reject any pending calls made to that stub.
 *
 * Timeouts
 * --------
 * TODO (Not yet implementd.)
 * You may call rpc.setTimeout(ms) or rpc.setStubTimeout(stub, ms) to set a call timeout for all
 * stubs or for a particular one. If a response to a call does not arrive within the timeout, the
 * call gets rejected, and the rejection Error will have a "code" property set to "TIMEOUT".
 *
 * Forwarding
 * ----------
 * Rpc.registerForwarder() along with methods with "-Forward" suffix allow one Rpc object to forward
 * calls and messages to another Rpc object. The intended usage is when Rpc connects A to B, and B
 * to C. Then B can use registerForwarder to expose A's interfaces to C (or C's to A) without having
 * to know what exactly they are. A default forwarder can be registered using the '*' name.
 *
 *
 * Instead of using getStubForward and callRemoteFuncForward, the forwarder name can be
 * appended to the interface name as "interfaceName@forwarderName" and the regular
 * getStub and callRemoteFunc methods can be used.  For example:
 *   getStub("iface@forwarder")
 * is the same as:
 *   getStubForward("forwarder", "iface")
 *
 *
 * E.g. with A.registerImpl("A-name", ...) and B.registerForwarder("b2a", A), we may now call
 * C.getStubForward("b2a", "A-name") to get a stub that will forward calls to A, as well as
 * C.postMessageForward("b2a", msg) to have the message received by A.
 *
 * TODO We may want to support progress callbacks, perhaps by supporting arbitrary callbacks as
 * parameters. (Could be implemented by allowing "meth" to be [reqId, paramPath]) It would be nice
 * to allow the channel to report progress too, e.g. to report progress of uploading large files.
 *
 * TODO Sending of large files should probably be a separate feature, to allow for channel
 * implementations to stream them.
 */
const events_1 = require("events");
const tic = require("ts-interface-checker");
const message_1 = require("./message");
const plainCall = (callFunc) => callFunc();
class Rpc extends events_1.EventEmitter {
    /**
     * To use Rpc, you must provide a sendMessage function that sends a message to the other side;
     * it may be given in the constructor, or later with setSendMessage. You must also call
     * receiveMessage() for every message received from the other side.
     */
    constructor(options = {}) {
        super();
        // Note the invariant: _inactiveSendQueue == null iff (_sendMessageCB != null && !_waitForReadyMessage)
        this._sendMessageCB = null;
        this._inactiveRecvQueue = null; // queue of received message
        this._inactiveSendQueue = null; // queue of messages to be sent
        this._waitForReadyMessage = false;
        this._implMap = new Map();
        this._forwarders = new Map();
        this._pendingCalls = new Map();
        this._nextRequestId = 1;
        const { logger = console, sendMessage = null, callWrapper = plainCall } = options;
        this._logger = logger;
        this._callWrapper = callWrapper;
        this.setSendMessage(sendMessage);
    }
    /**
     * To use Rpc, call this for every message received from the other side of the channel.
     */
    receiveMessage(msg) {
        if (this._inactiveRecvQueue) {
            this._inactiveRecvQueue.push(msg);
        }
        else {
            this._dispatch(msg);
        }
    }
    /**
     * If you've set up calls to receiveMessage(), but need time to call registerImpl() before
     * processing new messages, you may use queueIncoming(), make the registerImpl() calls,
     * and then call processIncoming() to handle queued messages and resume normal processing.
     */
    queueIncoming() {
        if (!this._inactiveRecvQueue) {
            this._inactiveRecvQueue = [];
        }
    }
    /**
     * Process received messages queued since queueIncoming, and resume normal processing of
     * received messages.
     */
    processIncoming() {
        if (this._inactiveRecvQueue) {
            processQueue(this._inactiveRecvQueue, this._dispatch.bind(this));
            this._inactiveRecvQueue = null;
        }
    }
    /**
     * Set the callback to send messages. If set to null, sent messages will be queued. If you
     * disconnect and want for sent messages to throw, set a callback that throws.
     */
    setSendMessage(sendMessage) {
        this._sendMessageCB = sendMessage;
        if (this._sendMessageCB) {
            this._processOutgoing();
        }
        else {
            this._queueOutgoing();
        }
    }
    /**
     * If your peer may not be listening yet to your messages, you may call this to queue outgoing
     * messages until receiving a "ready" message from the peer. I.e. one peer may call
     * queueOutgoingUntilReadyMessage() while the other calls sendReadyMessage().
     */
    queueOutgoingUntilReadyMessage() {
        this._waitForReadyMessage = true;
        this._queueOutgoing();
    }
    /**
     * If your peer is using queueOutgoingUntilReadyMessage(), you should let it know that you are
     * ready using sendReadyMessage() as soon as you've set up the processing of received messages.
     * Note that at most one peer may use queueOutgoingUntilReadyMessage(), or they will deadlock.
     */
    sendReadyMessage() {
        return this._sendMessage({ mtype: message_1.MsgType.Ready });
    }
    /**
     * Messaging interface: send data to the other side, to be emitted there as a "message" event.
     */
    postMessage(data) { return this.postMessageForward("", data); }
    postMessageForward(fwdDest, data) {
        return __awaiter(this, void 0, void 0, function* () {
            const msg = { mtype: message_1.MsgType.Custom, data };
            if (fwdDest) {
                msg.mdest = fwdDest;
            }
            yield this._sendMessage(msg);
        });
    }
    registerImpl(name, impl, checker) {
        if (this._implMap.has(name)) {
            throw new Error(`Rpc.registerImpl has already been called for ${name}`);
        }
        const invokeImpl = (call) => impl[call.meth](...call.args);
        if (!checker) {
            this._implMap.set(name, { name, invokeImpl, argsCheckers: null });
        }
        else {
            const ttype = checker.getType();
            if (!(ttype instanceof tic.TIface)) {
                throw new Error("Rpc.registerImpl requires a Checker for an interface");
            }
            const argsCheckers = {};
            for (const prop of ttype.props) {
                if (prop.ttype instanceof tic.TFunc) {
                    argsCheckers[prop.name] = checker.methodArgs(prop.name);
                }
            }
            this._implMap.set(name, { name, invokeImpl, argsCheckers });
        }
    }
    registerForwarder(fwdName, dest, fwdDest = (fwdName === "*" ? "*" : "")) {
        const passThru = fwdDest === "*";
        this._forwarders.set(fwdName, {
            name: "[FWD]" + fwdName,
            argsCheckers: null,
            invokeImpl: (c) => dest.forwardCall(Object.assign({}, c, { mdest: passThru ? c.mdest : fwdDest })),
            forwardMessage: (msg) => dest.forwardMessage(Object.assign({}, msg, { mdest: passThru ? msg.mdest : fwdDest })),
        });
    }
    unregisterForwarder(fwdName) {
        this._forwarders.delete(fwdName);
    }
    /**
     * Unregister an implementation, if one was registered with this name.
     */
    unregisterImpl(name) {
        this._implMap.delete(name);
    }
    getStub(name, checker) {
        const parts = this._parseName(name);
        return this.getStubForward(parts.forwarder, parts.name, checker);
    }
    getStubForward(fwdDest, name, checker) {
        if (!checker) {
            // TODO Test, then explain how this works.
            return new Proxy({}, {
                get: (target, property, receiver) => {
                    if (property === "then") {
                        // By default, take care not to look "thenable", so that the stub can be returned
                        // as a value of a Promise:
                        // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Promise/resolve
                        // If user really wants to proxy "then", they can write a checker.
                        return undefined;
                    }
                    return (...args) => this._makeCall(name, property, args, anyChecker, fwdDest);
                },
            });
        }
        else {
            const ttype = checker.getType();
            if (!(ttype instanceof tic.TIface)) {
                throw new Error("Rpc.getStub requires a Checker for an interface");
            }
            const api = {};
            for (const prop of ttype.props) {
                if (prop.ttype instanceof tic.TFunc) {
                    const resultChecker = checker.methodResult(prop.name);
                    api[prop.name] = (...args) => this._makeCall(name, prop.name, args, resultChecker, fwdDest);
                }
            }
            return api;
        }
    }
    /**
     * Simple way to registers a function under a given name, with no argument checking.
     */
    registerFunc(name, impl) {
        return this.registerImpl(name, { invoke: impl }, checkerAnyFunc);
    }
    /**
     * Unregister a function, if one was registered with this name.
     */
    unregisterFunc(name) {
        return this.unregisterImpl(name);
    }
    /**
     * Call a remote function registered with registerFunc. Does no type checking.
     */
    callRemoteFunc(name, ...args) {
        const parts = this._parseName(name);
        return this.callRemoteFuncForward(parts.forwarder, parts.name, ...args);
    }
    callRemoteFuncForward(fwdDest, name, ...args) {
        return this._makeCall(name, "invoke", args, anyChecker, fwdDest);
    }
    forwardCall(c) {
        return this._makeCall(c.iface, c.meth, c.args, anyChecker, c.mdest || "");
    }
    forwardMessage(msg) {
        return this.postMessageForward(msg.mdest || "", msg.data);
    }
    // Mark outgoing messages for queueing.
    _queueOutgoing() {
        if (!this._inactiveSendQueue) {
            this._inactiveSendQueue = [];
        }
    }
    // If sendMessageCB is set and we are no longer waiting for a ready message, send out any
    // queued outgoing messages and resume normal sending.
    _processOutgoing() {
        if (this._inactiveSendQueue && this._sendMessageCB && !this._waitForReadyMessage) {
            processQueue(this._inactiveSendQueue, this._sendMessageOrReject.bind(this, this._sendMessageCB));
            this._inactiveSendQueue = null;
        }
    }
    _sendMessage(msg) {
        if (this._inactiveSendQueue) {
            this._inactiveSendQueue.push(msg);
        }
        else {
            return this._sendMessageOrReject(this._sendMessageCB, msg);
        }
    }
    // This helper calls calls sendMessage(msg), and if that call fails, rejects the call
    // represented by msg (when it's of type RpcCall).
    _sendMessageOrReject(sendMessage, msg) {
        if (this._logger.info) {
            const desc = (msg.mtype === message_1.MsgType.RpcCall) ? ": " + this._callDesc(msg) : "";
            this._logger.info(`Rpc sending ${message_1.MsgType[msg.mtype]}${desc}`);
        }
        return catchMaybePromise(() => sendMessage(msg), (err) => this._sendReject(msg, err));
    }
    // Rejects a RpcCall due to the given send error; this helper always re-throws.
    _sendReject(msg, err) {
        const newErr = new ErrorWithCode("RPC_SEND_FAILED", `Send failed: ${err.message}`);
        if (msg.mtype === message_1.MsgType.RpcCall && msg.reqId !== undefined) {
            const callObj = this._pendingCalls.get(msg.reqId);
            if (callObj) {
                this._pendingCalls.delete(msg.reqId);
                callObj.reject(newErr);
            }
        }
        this.emit("error", newErr);
        throw newErr;
    }
    _makeCallRaw(iface, meth, args, resultChecker, fwdDest) {
        return new Promise((resolve, reject) => {
            const reqId = this._nextRequestId++;
            const callObj = { reqId, iface, meth, resolve, reject, resultChecker };
            this._pendingCalls.set(reqId, callObj);
            // Send the Call message. If the sending fails, reject the _makeCall promise. If it
            // succeeds, we save {resolve,reject} to resolve _makeCall when we get back a response.
            this._info(callObj, "RPC_CALLING");
            const msg = { mtype: message_1.MsgType.RpcCall, reqId, iface, meth, args };
            if (fwdDest) {
                msg.mdest = fwdDest;
            }
            // If _sendMessage fails, reject, allowing it to throw synchronously or not.
            catchMaybePromise(() => this._sendMessage(msg), reject);
        });
    }
    _makeCall(iface, meth, args, resultChecker, fwdDest) {
        return this._callWrapper(() => this._makeCallRaw(iface, meth, args, resultChecker, fwdDest));
    }
    _dispatch(msg) {
        switch (msg.mtype) {
            case message_1.MsgType.RpcCall: {
                this._onMessageCall(msg);
                return;
            }
            case message_1.MsgType.RpcRespData:
            case message_1.MsgType.RpcRespErr: {
                this._onMessageResp(msg);
                return;
            }
            case message_1.MsgType.Custom: {
                this._onCustomMessage(msg);
                return;
            }
            case message_1.MsgType.Ready: {
                this._waitForReadyMessage = false;
                try {
                    this._processOutgoing();
                }
                catch (e) { /* swallowing error, an event 'error' was already emitted */ }
                return;
            }
        }
    }
    _onCustomMessage(msg) {
        if (msg.mdest) {
            const impl = this._forwarders.get(msg.mdest) || this._forwarders.get("*");
            if (!impl) {
                this._warn(null, "RPC_UNKNOWN_FORWARD_DEST", `Unknown forward destination: ${msg.mdest}`);
            }
            else {
                impl.forwardMessage(msg);
            }
        }
        else {
            this.emit("message", msg.data);
        }
    }
    _onMessageCall(call) {
        return __awaiter(this, void 0, void 0, function* () {
            let impl;
            if (call.mdest) {
                impl = this._forwarders.get(call.mdest) || this._forwarders.get("*");
                if (!impl) {
                    return this._failCall(call, "RPC_UNKNOWN_FORWARD_DEST", `Unknown forward destination: ${call.mdest}`);
                }
            }
            else {
                impl = this._implMap.get(call.iface);
                if (!impl) {
                    return this._failCall(call, "RPC_UNKNOWN_INTERFACE", "Unknown interface");
                }
            }
            if (!impl.argsCheckers) {
                // No call or argument checking.
            }
            else {
                // Check the method name and argument types.
                if (!impl.argsCheckers.hasOwnProperty(call.meth)) {
                    return this._failCall(call, "RPC_UNKNOWN_METHOD", "Unknown method");
                }
                const argsChecker = impl.argsCheckers[call.meth];
                try {
                    argsChecker.check(call.args);
                }
                catch (e) {
                    return this._failCall(call, "RPC_INVALID_ARGS", `Invalid args: ${e.message}`);
                }
            }
            if (call.reqId === undefined) {
                return this._failCall(call, "RPC_MISSING_REQID", "Missing request id");
            }
            this._info(call, "RPC_ONCALL");
            let result;
            try {
                result = yield impl.invokeImpl(call);
            }
            catch (e) {
                return this._failCall(call, e.code, e.message, "RPC_ONCALL_ERROR");
            }
            this._info(call, "RPC_ONCALL_OK");
            return this._sendResponse(call.reqId, result);
        });
    }
    _failCall(call, code, mesg, reportCode) {
        return __awaiter(this, void 0, void 0, function* () {
            this._warn(call, reportCode || code, mesg);
            if (call.reqId !== undefined) {
                const msg = { mtype: message_1.MsgType.RpcRespErr, reqId: call.reqId, mesg, code };
                yield this._sendMessage(msg);
            }
        });
    }
    _sendResponse(reqId, data) {
        return __awaiter(this, void 0, void 0, function* () {
            const msg = { mtype: message_1.MsgType.RpcRespData, reqId, data };
            yield this._sendMessage(msg);
        });
    }
    _onMessageResp(resp) {
        const callObj = this._pendingCalls.get(resp.reqId);
        this._pendingCalls.delete(resp.reqId);
        if (!callObj) {
            this._warn(null, "RPC_UNKNOWN_REQID", `Response to unknown reqId ${resp.reqId}`);
            return;
        }
        if (resp.mtype === message_1.MsgType.RpcRespErr) {
            this._info(callObj, "RPC_RESULT_ERROR", resp.mesg);
            return callObj.reject(new ErrorWithCode(resp.code, resp.mesg));
        }
        try {
            callObj.resultChecker.check(resp.data);
        }
        catch (e) {
            this._warn(callObj, "RPC_RESULT_INVALID", e.message);
            return callObj.reject(new ErrorWithCode("RPC_INVALID_RESULT", `Implementation produced invalid result: ${e.message}`));
        }
        this._info(callObj, "RPC_RESULT_OK");
        callObj.resolve(resp.data);
    }
    _info(call, code, message) {
        if (this._logger.info) {
            const msg = message ? " " + message : "";
            this._logger.info(`Rpc for ${this._callDesc(call)}: ${code}${msg}`);
        }
    }
    _warn(call, code, message) {
        if (this._logger.warn) {
            const msg = message ? " " + message : "";
            this._logger.warn(`Rpc for ${this._callDesc(call)}: ${code}${msg}`);
        }
    }
    _callDesc(call) {
        if (!call) {
            return "?";
        }
        return `${call.iface}.${call.meth}#${call.reqId || "-"}`;
    }
    _parseName(name) {
        const idx = name.lastIndexOf("@");
        if (idx === -1) {
            return {
                forwarder: "",
                name,
            };
        }
        return {
            name: name.substr(0, idx),
            forwarder: name.substr(idx + 1),
        };
    }
}
exports.Rpc = Rpc;
/**
 * Interfaces may throw errors that include .code field, and it gets propagated to callers (e.g.
 * "NOT_AUTHORIZED"). Its purpose is to be a stable way to distinguish different types of errors.
 * This way the human-friendly error message can be changed without affecting behavior.
 */
class ErrorWithCode extends Error {
    constructor(code, message) {
        super(message);
        this.code = code;
    }
}
exports.ErrorWithCode = ErrorWithCode;
const IAnyFunc = tic.iface([], {
    invoke: tic.func("any"),
});
const { IAnyFunc: checkerAnyFunc } = tic.createCheckers({ IAnyFunc });
const checkerAnyResult = checkerAnyFunc.methodResult("invoke");
const anyChecker = checkerAnyResult;
/**
 * A little helper that processes message queues when starting an rpc instance.
 */
function processQueue(queue, processFunc) {
    let i = 0;
    try {
        while (i < queue.length) {
            // i gets read and then incremented before the call, so that if processFunc throws, the
            // message still gets removed from the queue (to avoid processing it twice).
            processFunc(queue[i++]);
        }
    }
    finally {
        queue.splice(0, i);
    }
}
/**
 * Calls callback(), handling the exception both synchronously and not. If callback and handler
 * both return or throw synchronously, then so does this method.
 */
function catchMaybePromise(callback, handler) {
    try {
        const p = callback();
        if (p) {
            return p.catch(handler);
        }
    }
    catch (err) {
        return handler(err);
    }
}
